import type { JSX } from "solid-js";
import { IconButton } from "~/common/button";
import clsx from "~/util/html/style";

type RemovableChipProps = {
  children: JSX.Element;
  /** Tooltip on the label when content can truncate. Defaults to the label string when children is a string. */
  title?: string;
  onRemove: () => void;
  /** Read-only: the label stays, the × is inert. */
  disabled?: boolean;
  /** "danger" marks a selection the server refused and the user must remove. */
  variant?: "danger";
  /** The label names a patient, so Blur sensitive data (features/dev_tools) redacts it, and the
   *  native tooltip, which would show it in clear, is left off. */
  sensitive?: boolean;
};

/**
 * Small filled label with a trailing × button. Used for selected items in
 * multi-select pickers (Combobox) and inline tag editors. Click on × calls
 * `onRemove`; propagation is stopped so parent click handlers (e.g. focus
 * the input) don't fire.
 */
export function RemovableChip(props: RemovableChipProps) {
  return (
    <span
      class={clsx(
        "flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-sm",
        props.variant === "danger" ? "bg-brand-danger-light" : "bg-brand-light",
      )}
    >
      <span
        class={clsx(
          "truncate",
          props.variant === "danger" ? "text-brand-danger" : "text-typography-primary",
        )}
        title={props.sensitive ? undefined : props.title}
        data-sensitive={props.sensitive ? "redact" : undefined}
      >
        {props.children}
      </span>
      <IconButton
        variant="plain"
        size="xs"
        iconName="x"
        aria-label="Remove"
        disabled={props.disabled}
        onClick={(e) => {
          e.stopPropagation();
          props.onRemove();
        }}
      />
    </span>
  );
}
